import { useQuery } from '@tanstack/react-query';
import {
  statDollarTransformer,
  statNumberTransformer,
  statNumberTransformerNoDecimal,
  statPercentTransformer,
} from 'pages/Shipments/Dashboard/OutstandingFees';
import { SectionTitle } from 'pages/Shipments/Dashboard/SectionTitle';
import React from 'react';
import {
  fetchOutstandingFeesKeyMetrics,
  InsightsQueryKeys,
  OutstandingFeesType,
} from 'services/clients/insights';
import { CurrentContainerStatus } from 'types/Watchlist';
import { buildSearchParams } from 'common/hooks/customSearchParams/customSearchParams';
import { SHIPMENTS_QUERY_PARAMS } from 'pages/Shipments/params';
import { Routes } from 'constants/pages';
import { useTrackedHistoricalParams } from '../params';
import { useCurrentDateRangeFilter } from './hooks';
import { Stat } from './KeyMetric';
import { KeyMetricCard } from './KeyMetricCard';

export const KeyMetricSection: React.FC = () => {
  const { searchParams } = useTrackedHistoricalParams();

  const dateRangeFilter = useCurrentDateRangeFilter();

  const keyMetricFilter = {
    type: OutstandingFeesType.PerDiem,
    calendarPeriod: searchParams.calendarPeriod,
    ...dateRangeFilter,
  };

  const outstandingQuery = useQuery({
    queryKey: [InsightsQueryKeys.OutstandingFeesKeyMetrics, keyMetricFilter],
    queryFn: ({ signal }) => fetchOutstandingFeesKeyMetrics(keyMetricFilter, signal),
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    },
  });

  const stats: Stat[] = [
    {
      title: 'Total accrued',
      value: statDollarTransformer(outstandingQuery.data?.totalAccrued),
      color: 'text-red-400',
    },
    {
      title: 'Dispute opportunity',
      // Backend sends this as a percentage, so we need to divide by 100, then format to clip off the decimals
      value: statPercentTransformer(
        outstandingQuery.data?.disputeOpportunity && outstandingQuery.data.disputeOpportunity / 100
      ),
      color: 'text-blue-700',
    },
    {
      title: 'Estimated savings',
      value: statDollarTransformer(outstandingQuery.data?.estimatedSavings),
      color: 'text-blue-700',
    },
    {
      title: 'Avg. per diem',
      value: statDollarTransformer(outstandingQuery.data?.avgPerDiem),
    },
    {
      title: 'Avg. days in per diem',
      value: statNumberTransformer(outstandingQuery.data?.avgDaysOfPerDiem),
    },
  ];

  const allContainers = statNumberTransformerNoDecimal(outstandingQuery.data?.totalContainers);

  const to = React.useMemo(() => {
    const params = buildSearchParams(SHIPMENTS_QUERY_PARAMS, {
      hasPerDiemFees: true,
      containerStatus: [CurrentContainerStatus.Terminated],
      gatedInAt: dateRangeFilter,
    });
    const urlParams = new URLSearchParams(params);
    return `${Routes.ShipmentsActive}?${urlParams.toString()}`;
  }, [dateRangeFilter]);

  return (
    <div className="flex flex-col space-y-4 px-4">
      <SectionTitle
        title="Key metrics"
        tooltip="Summary metrics of per diem charges accrued during the selected date range selected."
      />
      <div className="grid grid-cols-6 space-x-4">
        <KeyMetricCard title="Total # of containers" value={allContainers} to={to} />
        {stats.map((stat: Stat) => (
          <KeyMetricCard key={stat.title} {...stat} />
        ))}
      </div>
    </div>
  );
};
